import { cva, type VariantProps } from 'class-variance-authority';
import type { HTMLAttributes } from 'react';
import { GRMS_TAG_TONES, type GrmsTagTone } from '@/lib/grms/statusColors';
import { cn } from '@/lib/utils';

// `tag` is the short uppercase label of the room card, the floor plan, the
// housekeeping rows and the pages. `pill` is the sentence-case state word of the
// room dialog (room-dialog redesign D6): rounded-full, no uppercase, one size,
// with an optional leading dot or 13 px icon passed as children. Tones are shared.
const statusTagVariants = cva(
  'inline-flex items-center',
  {
    variants: {
      tone: GRMS_TAG_TONES,
      shape: {
        tag: 'gap-1 rounded-md px-1.5 py-0.5 font-display uppercase',
        pill: 'gap-1.5 rounded-full px-2.5 py-0.5 text-caption font-medium',
      },
      size: {
        sm: '',
        md: '',
      },
    },
    compoundVariants: [
      { shape: 'tag', size: 'sm', class: 'text-micro font-semibold' },
      { shape: 'tag', size: 'md', class: 'text-caption font-medium' },
    ],
    defaultVariants: {
      tone: 'neutral',
      shape: 'tag',
      size: 'sm',
    },
  },
);

export interface StatusTagProps
  extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof statusTagVariants> {
  tone?: GrmsTagTone;
}

export function StatusTag({ className, tone, shape, size, ...props }: StatusTagProps) {
  return <span className={cn(statusTagVariants({ tone, shape, size }), className)} {...props} />;
}

export { statusTagVariants };
